import React, { useState } from 'react';
import { FaCheckCircle, FaTimesCircle, FaShieldAlt, FaLink, FaChevronDown, FaChevronUp, FaDownload, FaSpinner } from 'react-icons/fa';

const VerificationCertificate = ({ certificate, onDownloadPDF, isDownloading }) => {
  const [expandedClaims, setExpandedClaims] = useState(new Set());

  if (!certificate) {
    return (
      <div className="flex items-center justify-center p-8">
        <FaSpinner className="animate-spin text-stanford-red text-2xl" />
      </div>
    );
  }

  const { document: doc, report, approvalChain, claimVerification, integrityStatus } = certificate;

  const toggleClaimExpand = (claimId) => {
    setExpandedClaims(prev => {
      const newSet = new Set(prev);
      if (newSet.has(claimId)) {
        newSet.delete(claimId);
      } else {
        newSet.add(claimId);
      }
      return newSet;
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      'admin': 'bg-blue-100 text-blue-800',
      'Regional Admin': 'bg-blue-100 text-blue-800',
      'auditor': 'bg-purple-100 text-purple-800',
      'Auditor': 'bg-purple-100 text-purple-800',
      'regional_director': 'bg-green-100 text-green-800',
      'Regional Director': 'bg-green-100 text-green-800',
      'global_director': 'bg-amber-100 text-amber-800',
      'Global Director': 'bg-amber-100 text-amber-800',
      'finance_director': 'bg-red-100 text-red-800',
      'Finance Director': 'bg-red-100 text-red-800'
    };
    return classes[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-stanford-red px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
          <div className="flex items-center gap-3 min-w-0">
            <FaShieldAlt className="text-2xl sm:text-3xl text-white flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-white">{doc.title}</h1>
              <p className="text-red-100 text-sm">Stanford Seed Expense Claim System</p>
            </div>
          </div>
          <button
            onClick={onDownloadPDF}
            disabled={isDownloading}
            className="flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {isDownloading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
            {isDownloading ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Report Summary */}
      <div className="px-4 py-4 sm:px-6 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="col-span-2 md:col-span-1 min-w-0">
            <span className="text-gray-500">Document ID</span>
            <p className="font-mono font-semibold text-gray-800">{doc.reportId}</p>
          </div>
          <div>
            <span className="text-gray-500">Region</span>
            <p className="font-semibold text-gray-800">{report.region}</p>
          </div>
          <div>
            <span className="text-gray-500">Period</span>
            <p className="font-semibold text-gray-800">{report.monthName} {report.year}</p>
          </div>
          <div>
            <span className="text-gray-500">Total Amount</span>
            <p className="font-semibold text-gray-800">{formatCurrency(report.totalAmount)}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {/* Label/value pairs stay together when the row wraps on a phone. */}
          <span className="inline-flex items-center gap-2 sm:gap-4">
          <span className="text-gray-500">Status:</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            report.status === 'global_director_approved' ? 'bg-green-100 text-green-800' :
            report.status === 'rejected' ? 'bg-red-100 text-red-800' :
            'bg-yellow-100 text-yellow-800'
          }`}>
            {report.statusDisplay}
          </span>
          </span>
          <span className="inline-flex items-center gap-2 sm:gap-4">
          <span className="text-gray-500">Version:</span>
          <span className="font-mono">{report.version}</span>
          </span>
          <span className="inline-flex items-center gap-2 sm:gap-4">
          <span className="text-gray-500">Claims:</span>
          <span className="font-mono">{report.claimsCount}</span>
          </span>
        </div>
      </div>

      {/* Approval Chain */}
      <div className="px-4 py-4 sm:px-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <FaLink className="text-stanford-red" />
          Approval Chain
        </h2>

        <div className="relative">
          {approvalChain.map((entry, index) => (
            <div key={entry.step} className="relative pl-8 pb-6 last:pb-0">
              {/* Vertical line */}
              {index < approvalChain.length - 1 && (
                <div className="absolute left-[15px] top-8 bottom-0 w-0.5 bg-gray-300" />
              )}

              {/* Step circle */}
              <div className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                entry.verified ? 'bg-green-500' : 'bg-red-500'
              }`}>
                {entry.step}
              </div>

              {/* Content */}
              <div className="bg-gray-50 rounded-lg p-3 ml-2 sm:p-4 sm:ml-4 min-w-0">
                <div className="flex flex-wrap sm:flex-nowrap items-start justify-between gap-x-2 gap-y-1">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-800">{entry.title}</h3>
                    <p className="text-sm text-gray-500">{entry.description}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {entry.verified ? (
                      <FaCheckCircle className="text-green-500" />
                    ) : (
                      <FaTimesCircle className="text-red-500" />
                    )}
                    <span className={`text-xs font-medium ${entry.verified ? 'text-green-600' : 'text-red-600'}`}>
                      {entry.verified ? 'Verified' : 'Failed'}
                    </span>
                  </div>
                </div>

                {entry.performedBy && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-gray-500">By:</span>
                    <span className="font-medium">{entry.performedBy.name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getRoleBadgeClass(entry.performedBy.roleDisplay)}`}>
                      {entry.performedBy.roleDisplay}
                    </span>
                  </div>
                )}

                <div className="mt-2 text-sm text-gray-500">
                  <span>{entry.timestampFormatted}</span>
                </div>

                {entry.comments && (
                  <div className="mt-2 p-2 bg-white rounded border border-gray-200 text-sm">
                    <span className="text-gray-500">Comments:</span>
                    <p className="text-gray-700 italic">"{entry.comments}"</p>
                  </div>
                )}

                {/* Hash display */}
                <div className="mt-3 space-y-1">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-2">
                    <span className="text-xs text-gray-500 w-20 flex-shrink-0">Hash:</span>
                    <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded break-all text-gray-700">
                      {entry.hash}
                    </code>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-2">
                    <span className="text-xs text-gray-500 w-20 flex-shrink-0">Previous:</span>
                    <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded break-all text-gray-500">
                      {entry.previousHash === 'GENESIS' ? (
                        <span className="text-green-600 font-semibold">GENESIS (Chain Start)</span>
                      ) : (
                        entry.previousHash
                      )}
                    </code>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Claim Verification */}
      <div className="px-4 py-4 sm:px-6 border-t border-gray-200">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Individual Claim Verification</h2>

        <div className="space-y-2">
          {claimVerification.map((claim) => (
            <div key={claim.reportClaimId} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleClaimExpand(claim.reportClaimId)}
                aria-expanded={expandedClaims.has(claim.reportClaimId)}
                className="w-full px-3 py-3 sm:px-4 flex items-center justify-between gap-2 sm:gap-0 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex flex-1 flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-0.5 min-w-0 sm:flex-initial">
                  {claim.integrityValid ? (
                    <FaCheckCircle className="text-green-500 flex-shrink-0" />
                  ) : (
                    <FaTimesCircle className="text-red-500 flex-shrink-0" />
                  )}
                  <div className="text-left flex-1 min-w-0 sm:flex-initial">
                    <span className="font-medium text-gray-800">{claim.employeeName}</span>
                    <span className="text-gray-500 ml-2">- {claim.claimTypeDisplay}</span>
                  </div>
                  <span className="basis-full pl-7 text-left sm:basis-auto sm:pl-0 font-mono text-sm text-gray-600">{formatCurrency(claim.amount)}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-gray-500 whitespace-nowrap">{claim.chainLength} entries</span>
                  {expandedClaims.has(claim.reportClaimId) ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </button>

              {expandedClaims.has(claim.reportClaimId) && (
                <div className="px-3 py-3 sm:px-4 bg-white border-t border-gray-200">
                  <div className="mb-3">
                    <span className="text-xs text-gray-500">Current Hash:</span>
                    <code className="block text-xs font-mono bg-gray-100 px-2 py-1 rounded mt-1 break-all">
                      {claim.currentHash || 'No hash generated - run backfill utility'}
                    </code>
                  </div>

                  {claim.hasSplits && claim.splits ? (
                    <div className="mb-3">
                      <span className="text-xs text-gray-500">Splits:</span>
                      <div className="mt-1 space-y-1">
                        {claim.splits.map((split, idx) => (
                          <div key={idx} className="flex flex-wrap sm:flex-nowrap items-center gap-2 text-sm pl-2 border-l-2 border-stanford-red">
                            <span className="text-gray-600">{formatCurrency(split.amount)}</span>
                            {split.ptae && (
                              <span className="font-mono text-stanford-red">{split.ptae}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : claim.ptae && (
                    <div className="mb-3">
                      <span className="text-xs text-gray-500">PTAE:</span>
                      <span className="ml-2 font-mono text-sm text-stanford-red">{claim.ptae}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <span className="text-xs text-gray-500">Hash Chain:</span>
                    {claim.hashChain && claim.hashChain.length > 0 ? (
                      claim.hashChain.map((entry, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs pl-2 border-l-2 border-gray-200">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-2 gap-y-0.5">
                              {entry.verified ? (
                                <FaCheckCircle className="text-green-500 text-xs flex-shrink-0" />
                              ) : (
                                <FaTimesCircle className="text-red-500 text-xs flex-shrink-0" />
                              )}
                              <span className="font-medium capitalize">{entry.stage.replace(/_/g, ' ')}</span>
                              <span className="text-gray-400">by {entry.performer}</span>
                            </div>
                            <code className="font-mono text-gray-500 break-all">{entry.hash}</code>
                            <div className="text-gray-400 mt-0.5">{entry.timestampFormatted}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                        No hash chain entries found for this claim. Run the backfill utility to populate hash chain data for existing claims.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Integrity Status */}
      <div className="px-4 py-4 sm:px-6 border-t border-gray-200">
        <div className={`rounded-lg p-3 sm:p-4 ${integrityStatus.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-3 mb-3">
            {integrityStatus.valid ? (
              <FaCheckCircle className="text-green-500 text-2xl flex-shrink-0" />
            ) : (
              <FaTimesCircle className="text-red-500 text-2xl flex-shrink-0" />
            )}
            <div>
              <h3 className={`font-bold ${integrityStatus.valid ? 'text-green-800' : 'text-red-800'}`}>
                {integrityStatus.valid ? 'DOCUMENT INTEGRITY VERIFIED' : 'INTEGRITY COMPROMISED'}
              </h3>
              <p className={`text-sm ${integrityStatus.valid ? 'text-green-600' : 'text-red-600'}`}>
                {integrityStatus.valid
                  ? 'All hashes verified - no tampering detected'
                  : 'WARNING: Some hashes failed verification'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mt-4">
            <div>
              <span className="text-gray-500">Digital Fingerprint:</span>
              <code className="block font-mono text-xs bg-white/50 px-2 py-1 rounded mt-1 break-all">
                {integrityStatus.digitalFingerprint || 'Not generated'}
              </code>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Chain Length:</span>
                <span className="font-mono">{integrityStatus.chainLength} entries</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Claims Verified:</span>
                <span className="font-mono">{integrityStatus.claimsVerified}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500 flex-shrink-0">Last Verified:</span>
                <span className="text-xs text-right">{integrityStatus.lastVerifiedFormatted}</span>
              </div>
            </div>
          </div>

          {integrityStatus.errors && integrityStatus.errors.length > 0 && (
            <div className="mt-4 p-3 bg-red-100 rounded">
              <h4 className="font-semibold text-red-800 mb-2">Errors Detected:</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {integrityStatus.errors.map((error, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <FaTimesCircle className="text-red-500 mt-0.5 flex-shrink-0" />
                    <span>{error.error || error.message || JSON.stringify(error)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 sm:px-6 bg-gray-50 border-t border-gray-200 text-center text-xs text-gray-500">
        <p>Certificate generated: {doc.generatedAtFormatted}</p>
        <p>{doc.generatedBy} v{doc.version}</p>
      </div>
    </div>
  );
};

export default VerificationCertificate;
